import { Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";

import { INTERACTION } from "../login-utils/flowInteractions";
import { getOrganizationsFromFlow } from "../login-utils/organizationPickerUtils";
import { primaryButtonSx } from "./LoginChrome";
import type { ByosScreenProps } from "../login-utils/types";

function getSelectedTenant(form: Record<string, unknown>): string {
	const v = form.tenant ?? form.tenantId;
	return typeof v === "string" ? v : "";
}

export default function OrganizationPicker({
	onNext,
	form,
	onFormUpdate,
	context,
	errorText,
	isSubmitting = false
}: ByosScreenProps) {
	const orgs = useMemo(() => getOrganizationsFromFlow(context, form), [context, form]);
	const [selected, setSelected] = useState(getSelectedTenant(form));
	const handleContinue = async () => {
		if (!selected) return;
		onFormUpdate({ tenant: selected, tenantId: selected });
		await onNext(INTERACTION.TENANT_SELECTED, { tenant: selected, tenantId: selected });
	};

	return (
		<Stack sx={{ gap: 3, mt: 1 }}>
			<Box>
				<Typography
					sx={{
						fontSize: 20,
						fontWeight: 600,
						color: "rgba(13, 31, 48, 0.92)",
						textAlign: "center",
						letterSpacing: "-0.01em",
						mb: 0.5
					}}
				>
					Pick your organization
				</Typography>
				<Typography
					sx={{
						fontSize: 14,
						color: "rgba(13, 31, 48, 0.55)",
						textAlign: "center",
						lineHeight: 1.5
					}}
				>
					You're a member of more than one. Choose where you'd like to go.
				</Typography>
			</Box>

			<FormControl fullWidth disabled={orgs.length === 0}>
				<InputLabel
					shrink
					sx={{
						color: "rgba(13, 31, 48, 0.55)",
						"&.Mui-focused": { color: "#057A6D" }
					}}
				>
					Organization
				</InputLabel>
				<Select
					value={selected}
					label="Organization"
					displayEmpty
					onChange={(e) => setSelected(e.target.value as string)}
					renderValue={(value) => {
						if (!value) {
							return (
								<Typography sx={{ color: "rgba(13, 31, 48, 0.40)" }}>
									Select an organization…
								</Typography>
							);
						}
						const org = orgs.find((o) => o.id === value);
						return org?.name ?? value;
					}}
					sx={{
						borderRadius: 2,
						background: "#FFFFFF",
						"& .MuiOutlinedInput-notchedOutline": {
							borderColor: "rgba(13, 31, 48, 0.14)",
							transition: "border-color 0.18s"
						},
						"&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(2, 213, 171, 0.50)" },
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#02D5AB", borderWidth: 1.5 }
					}}
					MenuProps={{
						slotProps: {
							paper: {
								sx: {
									mt: 0.5,
									borderRadius: 2,
									boxShadow: "0 8px 32px rgba(13, 31, 48, 0.10), 0 1px 3px rgba(13, 31, 48, 0.04)"
								}
							}
						}
					}}
				>
					{orgs.map((org) => (
						<MenuItem
							key={org.id}
							value={org.id}
							sx={{
								py: 1.25,
								alignItems: "flex-start",
								"&.Mui-selected": { background: "rgba(2, 213, 171, 0.10)" },
								"&.Mui-selected:hover": { background: "rgba(2, 213, 171, 0.14)" }
							}}
						>
							<Stack>
								<Typography sx={{ fontSize: 14, fontWeight: 600, color: "rgba(13, 31, 48, 0.92)" }}>
									{org.name}
								</Typography>
								{org.sub && (
									<Typography sx={{ fontSize: 12, color: "rgba(13, 31, 48, 0.55)" }}>
										{org.sub}
									</Typography>
								)}
							</Stack>
						</MenuItem>
					))}
				</Select>
			</FormControl>

			{errorText && (
				<Typography sx={{ fontSize: 13, color: "#C04A4A", textAlign: "center" }}>
					{errorText}
				</Typography>
			)}

			<Button
				onClick={() => void handleContinue()}
				disabled={!selected || isSubmitting || orgs.length === 0}
				sx={primaryButtonSx}
				fullWidth
			>
				{isSubmitting ? "Loading…" : "Continue"}
			</Button>
		</Stack>
	);
}
